import { useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, CircularProgress, Typography } from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import RightArrowIcon from '@mui/icons-material/ChevronRightRounded';

import Input from 'shared/components/widgets/input';
import Button from 'shared/components/widgets/button';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';

import { ROUTES } from 'constants/routes';
import RouteLink from 'shared/components/widgets/route-link';
import InputContainer from 'shared/components/widgets/input-container';
import { Controller, useForm } from 'react-hook-form';
import { validationCodeIssueService } from '@services/private/password/validation-code-issue.service';
import { is200xRange } from '@utils/utilities';
import { validationCodeCheckService } from '@services/private/password/validation-code-check.service';
import { passswordChangeService } from '@services/private/password/password-change.service';
import { TOAST_MESSAGE } from 'constants/toast-message';
import { VALIDATION_MESSAGE } from 'constants/validation/message';
import PATTERN from 'constants/validation/pattern';

const ChangePassword = () => {
  const [isLoading, setLoading] = useState<boolean>()
  const { NEW_PASSWORD } =
    TOAST_MESSAGE.ADMIN.ACCOUNT_INFO.CHANGE_PASSWORD;

  const { CURRENT_PWD, NEW_PWD, CONFIRM_PWD } =
    VALIDATION_MESSAGE.ACCOUNT_INFO.ADMIN.CHANGE_PASSWORD;

  const [validationCode, setValidationCode] = useState(null);

  const changePasswordSchemaFields = validationCode
    ? {
      newPwd: yup
        .string()
        .required(NEW_PWD.REQUIRED)
        .min(9, NEW_PWD.MIN_LENGTH)
        .max(20, NEW_PWD.MAX_LENGTH),
      confirmPwd: yup
        .string()
        .required(CONFIRM_PWD.REQUIRED)
        .oneOf(
          // @ts-ignore
          [yup.ref('newPwd'), null],
          '변경할 비밀번호 확인 값이 틀립니다.',
        ),
    }
    : {
      currentPwd: yup
        .string()
        .required(CURRENT_PWD.REQUIRED)
        .min(9, CURRENT_PWD.MIN_LENGTH)
        .max(20, CURRENT_PWD.MAX_LENGTH)
        .matches(PATTERN.LOWERCASE_LEAST, CURRENT_PWD.ATLEAST_LOWER)
        .matches(PATTERN.UPPERCASE_LEAST, CURRENT_PWD.ATLEAST_UPPER)
        .matches(PATTERN.NUMBER_LEAST, CURRENT_PWD.ATLEAST_NUMBER)
        .matches(PATTERN.SYMBOL_LEAST, CURRENT_PWD.ATLEAST_SPECIAL),
    };
  // @ts-ignore
  const changePasswordSchema = yup.object(changePasswordSchemaFields);

  type ChangePasswordFormData = yup.InferType<typeof changePasswordSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
    resetField
  } = useForm<ChangePasswordFormData>({
    defaultValues: {
      currentPwd: '',
      newPwd: '',
      confirmPwd: ''
    },
    resolver: yupResolver(changePasswordSchema),
  });

  const navigate = useNavigate();

  const checkPassword = async (currentPwd: string) => {
    setLoading(true)
    const { response, statusCode, error } = await validationCodeIssueService({
      currentPwd,
    });
    if (error || !is200xRange(statusCode)) {
      setLoading(false)
      // console.log(currentPwd)
      // toast.error('CURRENT_PASSWORD.FAILED');
    }

    if (response?.code === '1' && is200xRange(statusCode)) {
      resetField('currentPwd')
      resetField('newPwd')
      setValidationCode(response?.result?.checkInfo?.validationCode);
      setLoading(false)
    }
  };

  const updatePassword = async (validationCode, newPwd) => {
    setLoading(true)
    const { response, statusCode } = await validationCodeCheckService({
      validationCode,
    });

    if (response?.code === '1' && is200xRange(statusCode)) {
      const { response, statusCode, error } = await passswordChangeService({
        changePwd: newPwd,
      });
      if (error || !is200xRange(statusCode)) {
        toast.error(NEW_PASSWORD.FAILED);
        setLoading(false)
      }

      if (response?.code === '1' && is200xRange(statusCode)) {
        setLoading(false)
        navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT);
      }
    }
  };

  const onSubmit = (data: any) => {
    const { currentPwd, newPwd, confirmPwd } = data;
    if (validationCode) {
      if (confirmPwd === newPwd) {
        updatePassword(validationCode, newPwd);
      }
    } else {
      checkPassword(currentPwd);
    }
  };

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box marginBottom="18px">
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<RightArrowIcon sx={{ color: 'var(--neutral-800)' }} />}
        >
          <RouteLink to={ROUTES.ACCOUNT_INFO.CHANGE_PASSWORD.path}>
            계정정보
          </RouteLink>
          <RouteLink to={ROUTES.ACCOUNT_INFO.CHANGE_PASSWORD.path} isActive>
            비밀번호 변경
          </RouteLink>
        </Breadcrumbs>
      </Box>
      <Box marginBottom="20px">
        <Typography variant="h6" color="var(--base-black)" fontWeight={700}>
          비밀번호 변경
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={6} marginBottom="50px">
          {validationCode ? (
            <FieldBox>
              {/* CHANGE PASSWORD */}
              <FieldBoxProperty
                name="* 변경할 비밀번호"
                value={
                  <Controller
                    name="newPwd"
                    control={control}
                    render={({ field }) => {
                      return (
                        <InputContainer type="form">
                          {/* @ts-ignore */}
                          <Input
                            {...field}
                            autoComplete={'new-password'}
                            placeholder="비밀번호를 입력해 주세요."
                            showErrorMsg={!!errors.newPwd}
                            errorMessage={
                              errors.newPwd && errors.newPwd.message
                            }
                            isPassword
                          />
                        </InputContainer>
                      );
                    }}
                  />
                }
              />
              {/* CONFIRM TO CHANGE PASSWORD */}
              <FieldBoxProperty
                name="* 변경할 비밀번호 확인"
                value={
                  <Controller
                    name="confirmPwd"
                    control={control}
                    render={({ field }) => {
                      return (
                        <InputContainer type="form">
                          {/* @ts-ignore */}
                          <Input
                            {...field}
                            placeholder="비밀번호를 입력해 주세요."
                            showErrorMsg={!!errors.confirmPwd}
                            errorMessage={
                              errors.confirmPwd && errors.confirmPwd.message
                            }
                            isPassword
                          />
                        </InputContainer>
                      );
                    }}
                  />
                }
              />
            </FieldBox>
          ) : (
            <FieldBox>
              {/* CURRENT PASSWORD */}
              <FieldBoxProperty
                name="* 현재 비밀번호"
                value={
                  <Controller
                    name="currentPwd"
                    control={control}
                    render={({ field }) => {
                      return (
                        <InputContainer type="form">
                          {/* @ts-ignore */}
                          <Input
                            {...field}
                            placeholder="비밀번호를 입력해 주세요."
                            showErrorMsg={!!errors.currentPwd}
                            errorMessage={
                              errors.currentPwd && errors.currentPwd.message
                            }
                            isPassword
                          />
                        </InputContainer>
                      );
                    }}
                  />
                }
              />
            </FieldBox>
          )}
        </Box>
        <Box>
          {validationCode ? (
            <Box display="flex" gap="14px">
              {/* CANCEL */}
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  resetField('currentPwd')
                  resetField('newPwd')
                  resetField('confirmPwd')
                  setValidationCode(null);
                }}
              >
                취소
              </Button>
              {/* EDIT */}
              <Button
                disabled={isLoading}
                variant="contained"
                color="primary"
                sx={{ minWidth: '86px' }}
                type="submit"
              >
                {isLoading ?
                  <CircularProgress size={18} color='secondary' />
                  :
                  '수정하기'
                }
              </Button>
            </Box>
          ) : (
            <Box display="flex" gap="14px">
              {/* CANCEL */}
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  navigate(-1);
                }}
              >
                취소
              </Button>
              {/* CHECK */}
              <Button
                disabled={isLoading}
                variant="contained"
                color="primary"
                sx={{ minWidth: '86px' }}
                type="submit"
              >
                {isLoading ?
                  <CircularProgress size={18} color='secondary' />
                  :
                  '확인'
                }
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChangePassword;
